import { sha256Hex } from "./crypto.js";

// Sync payloads can exceed one QR code. Frames carry a short session id (hash
// prefix of the payload) so frames from different payloads cannot silently mix.
// Frame format: PS1|<sid>|<i>/<n>|<chunk>   (i is 1-based)
//
// PS2 frames use the same framing but carry the compact payload encoding
// (see the app's wire layer). The distinct prefix makes clients that predate
// it fail on the FIRST frame instead of assembling a payload they cannot read.

export const QR_FRAME_PREFIX = "PS1";
export const QR_FRAME_PREFIX_V2 = "PS2";
export const DEFAULT_QR_CHUNK = 700; // chars per frame; conservative for phone scanning

export function chunkForQr(
  payload: string,
  maxChunk: number = DEFAULT_QR_CHUNK,
  prefix: string = QR_FRAME_PREFIX,
): string[] {
  if (maxChunk < 16) throw new Error("chunk size too small");
  const sid = sha256Hex(payload).slice(0, 8);
  const total = Math.max(1, Math.ceil(payload.length / maxChunk));
  const frames: string[] = [];
  for (let i = 0; i < total; i++) {
    frames.push(
      `${prefix}|${sid}|${i + 1}/${total}|${payload.slice(i * maxChunk, (i + 1) * maxChunk)}`,
    );
  }
  return frames;
}

export interface AssembleStatus {
  ok: boolean;
  done: boolean;
  have: number;
  total: number;
  error?: string;
}

export class QrAssembler {
  private sid = "";
  private total = 0;
  private chunks = new Map<number, string>();

  add(frame: string): AssembleStatus {
    const status = (error?: string): AssembleStatus => ({
      ok: error === undefined,
      done: this.total > 0 && this.chunks.size === this.total,
      have: this.chunks.size,
      total: this.total,
      ...(error === undefined ? {} : { error }),
    });
    const parts = frame.split("|");
    if (parts.length !== 4 || (parts[0] !== QR_FRAME_PREFIX && parts[0] !== QR_FRAME_PREFIX_V2)) {
      return status("not a psplit frame");
    }
    const sid = parts[1] as string;
    const idx = parts[2] as string;
    const chunk = parts[3] as string;
    const m = /^(\d+)\/(\d+)$/.exec(idx);
    if (!m) return status("bad frame index");
    const i = Number(m[1]);
    const n = Number(m[2]);
    if (!Number.isSafeInteger(i) || !Number.isSafeInteger(n) || i < 1 || n < 1 || i > n) {
      return status("bad frame index");
    }
    if (this.sid === "") {
      this.sid = sid;
      this.total = n;
    } else if (sid !== this.sid) {
      return status("frame belongs to a different payload");
    } else if (n !== this.total) {
      return status("frame count mismatch");
    }
    this.chunks.set(i, chunk);
    return status();
  }

  get done(): boolean {
    return this.total > 0 && this.chunks.size === this.total;
  }

  payload(): string {
    if (!this.done) throw new Error("payload incomplete");
    let out = "";
    for (let i = 1; i <= this.total; i++) out += this.chunks.get(i) as string;
    // Integrity: the sid must match what the sender derived from the payload.
    if (sha256Hex(out).slice(0, 8) !== this.sid) throw new Error("payload checksum mismatch");
    return out;
  }
}
